import React, { useRef, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { X } from 'lucide-react';
import { useDiagramStore } from '../../stores/diagramStore';
import { cn } from '../../lib/utils';
import { Logger } from '../../utils/logger';
import { useOrgChartViewStore } from '../../stores/orgchartViewStore';
import {
  findPoolTable,
  getPoolLaneDirection,
  readPoolLaneBounds,
} from '../../domains/process/services/laneContainment';

const logger = Logger.getLoggerWithPrefix('Minimap');

interface MinimapProps {
  onClose: () => void;
  onPanTo?: (x: number, y: number) => void;
  hideHeader?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const Minimap: React.FC<MinimapProps> = ({ onClose, onPanTo, hideHeader = false, className, style }) => {
  const { t } = useTranslation('common');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stage = useDiagramStore(s => s.stage);
  const nodes = useDiagramStore(s => s.nodes);
  const collapsedNodeIds = useOrgChartViewStore(s => s.collapsedNodeIds);
  const [isDragging, setIsDragging] = React.useState(false);

  // Minimap dimensions
  const minimapWidth = 240;
  const minimapHeight = 180;

  const visibleNodes = React.useMemo(
    () => (((window as any).__minimapNodes as Array<any> | undefined) ?? nodes),
    [nodes, collapsedNodeIds],
  );

  // Calculate content bounds from visible canvas nodes (org uses collapsed projection).
  const contentBounds = React.useMemo(() => {
    if (visibleNodes.length === 0) {
      return { x: 0, y: 0, width: 800, height: 600 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    visibleNodes.forEach(node => {
      const x = node.x || 0;
      const y = node.y || 0;
      const w = node.w || node.width || 100;
      const h = node.h || node.height || 100;

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    });

    const padding = 100;
    return {
      x: minX - padding,
      y: minY - padding,
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2
    };
  }, [visibleNodes]);

  const minimapScale = React.useMemo(
    () => Math.min(minimapWidth / contentBounds.width, minimapHeight / contentBounds.height),
    [minimapWidth, minimapHeight, contentBounds.width, contentBounds.height],
  );
  const minimapOffsetX = React.useMemo(
    () => (minimapWidth - contentBounds.width * minimapScale) / 2,
    [minimapWidth, contentBounds.width, minimapScale],
  );
  const minimapOffsetY = React.useMemo(
    () => (minimapHeight - contentBounds.height * minimapScale) / 2,
    [minimapHeight, contentBounds.height, minimapScale],
  );

  // Draw minimap directly from visible nodes and viewport.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      logger.warn('Canvas ref not available');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = minimapWidth;
    canvas.height = minimapHeight;

    // Clear canvas
    ctx.fillStyle = '#F7F8FA';
    ctx.fillRect(0, 0, minimapWidth, minimapHeight);
    const mappedX = (x: number) => minimapOffsetX + (x - contentBounds.x) * minimapScale;
    const mappedY = (y: number) => minimapOffsetY + (y - contentBounds.y) * minimapScale;

    // Draw node silhouettes so minimap is stable regardless of stage transform.
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.65)';
    ctx.fillStyle = 'rgba(148, 163, 184, 0.22)';
    ctx.lineWidth = 1;
    const poolTable = findPoolTable(visibleNodes);

    for (const node of visibleNodes) {
      const attrs = (node?.attrs ?? {}) as Record<string, unknown>;
      if (attrs.__isPoolTable === true || (poolTable && node.id === poolTable.id)) continue;

      const x = typeof node?.x === 'number' ? node.x : 0;
      const y = typeof node?.y === 'number' ? node.y : 0;
      const w = (typeof node?.w === 'number' ? node.w : typeof node?.width === 'number' ? node.width : 100) || 100;
      const h = (typeof node?.h === 'number' ? node.h : typeof node?.height === 'number' ? node.height : 80) || 80;
      const rx = mappedX(x);
      const ry = mappedY(y);
      const rw = Math.max(1, w * minimapScale);
      const rh = Math.max(1, h * minimapScale);
      ctx.fillRect(rx, ry, rw, rh);
      ctx.strokeRect(rx, ry, rw, rh);
    }

    if (poolTable) {
      const px = typeof poolTable.x === 'number' ? poolTable.x : 0;
      const py = typeof poolTable.y === 'number' ? poolTable.y : 0;
      const pw =
        (typeof poolTable.w === 'number' ? poolTable.w : typeof poolTable.width === 'number' ? poolTable.width : 100) ||
        100;
      const ph =
        (typeof poolTable.h === 'number' ? poolTable.h : typeof poolTable.height === 'number' ? poolTable.height : 80) ||
        80;
      const prx = mappedX(px);
      const pry = mappedY(py);
      const prw = Math.max(1, pw * minimapScale);
      const prh = Math.max(1, ph * minimapScale);

      ctx.strokeStyle = 'rgba(100, 116, 139, 0.85)';
      ctx.lineWidth = 1.25;
      ctx.strokeRect(prx, pry, prw, prh);

      const direction = getPoolLaneDirection(poolTable);
      const laneBounds = readPoolLaneBounds(poolTable, direction);
      if (laneBounds && laneBounds.length > 1) {
        ctx.strokeStyle = 'rgba(148, 163, 184, 0.55)';
        ctx.lineWidth = 1;
        const headerBand = laneBounds[0];
        if (headerBand) {
          if (direction === 'horizontal') {
            const hy = pry + headerBand.size * minimapScale;
            ctx.beginPath();
            ctx.moveTo(prx, hy);
            ctx.lineTo(prx + prw, hy);
            ctx.stroke();
          } else {
            const hx = prx + headerBand.size * minimapScale;
            ctx.beginPath();
            ctx.moveTo(hx, pry);
            ctx.lineTo(hx, pry + prh);
            ctx.stroke();
          }
        }
        for (let i = 1; i < laneBounds.length; i++) {
          const band = laneBounds[i]!;
          if (direction === 'horizontal') {
            const ly = pry + band.start * minimapScale;
            ctx.beginPath();
            ctx.moveTo(prx, ly);
            ctx.lineTo(prx + prw, ly);
            ctx.stroke();
          } else {
            const lx = prx + band.start * minimapScale;
            ctx.beginPath();
            ctx.moveTo(lx, pry);
            ctx.lineTo(lx, pry + prh);
            ctx.stroke();
          }
        }
      }
    }

    // Draw viewport rectangle (relative to content bounds)
    const viewportX = mappedX(-stage.offsetX / stage.scale);
    const viewportY = mappedY(-stage.offsetY / stage.scale);
    const stageNode = (window as any).__konvaStage;
    const stageWidth = typeof stageNode?.width === 'function' ? stageNode.width() : window.innerWidth;
    const stageHeight = typeof stageNode?.height === 'function' ? stageNode.height() : window.innerHeight;
    const viewportW = (stageWidth / stage.scale) * minimapScale;
    const viewportH = (stageHeight / stage.scale) * minimapScale;

    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.strokeRect(viewportX, viewportY, viewportW, viewportH);
    ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
    ctx.fillRect(viewportX, viewportY, viewportW, viewportH);
  }, [
    stage,
    minimapWidth,
    minimapHeight,
    visibleNodes,
    contentBounds,
    minimapScale,
    minimapOffsetX,
    minimapOffsetY,
  ]);

  // Handle click/drag to pan
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    handlePan(e);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    handlePan(e);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handlePan = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onPanTo) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert minimap coordinates to diagram coordinates (account for content bounds offset)
    const diagramX = (clickX - minimapOffsetX) / minimapScale + contentBounds.x;
    const diagramY = (clickY - minimapOffsetY) / minimapScale + contentBounds.y;

    onPanTo(diagramX, diagramY);
  };

  if (hideHeader) {
    return (
      <div ref={containerRef}>
        <canvas
          ref={canvasRef}
          className="border rounded cursor-pointer bg-white dark:bg-gray-900"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "fixed bottom-20 right-6 z-50",
        "bg-background border rounded-lg shadow-xl p-3",
        "animate-in slide-in-from-bottom-5 duration-200",
        className
      )}
      style={style}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-foreground">{t('ui.overview')}</h3>
        <button
          onClick={onClose}
          className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted transition-colors"
          aria-label={t('panels.closeMinimap')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <canvas
        ref={canvasRef}
        className="border rounded cursor-pointer bg-white dark:bg-gray-900"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
    </div>
  );
};
